"use client";

import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { Truck } from "@/components/icons";
import { FreightPill, LoadInvoice, RequestBubble } from "./landing-hero-quote";
import { US_CITY_POINTS, US_DOT_PATH, US_MAP_HEIGHT, US_MAP_WIDTH } from "./landing-network-map";
import { type DemoCity, type DemoScenario, useQuoteDemo } from "./use-quote-demo";

const AMBIENT_LANES: Array<{ from: DemoCity; to: DemoCity; seconds: number }> = [
  { from: "minneapolis", to: "columbus", seconds: 5.2 },
  { from: "saltLake", to: "denver", seconds: 4.4 },
  { from: "seattle", to: "sacramento", seconds: 5.8 },
  { from: "newark", to: "charlotte", seconds: 4.8 },
  { from: "denver", to: "kansasCity", seconds: 5 },
  { from: "orlando", to: "miami", seconds: 3.6 },
  { from: "detroit", to: "newark", seconds: 5.6 },
  { from: "kansasCity", to: "stGeorge", seconds: 6.2 },
];

const LIFT = 0.28;
const TRUCK_SECONDS = 2.4;
const WEST_EDGE = 220;
const EAST_EDGE = 800;

function laneControl(from: DemoCity, to: DemoCity): [number, number] {
  const [startX, startY] = US_CITY_POINTS[from];
  const [endX, endY] = US_CITY_POINTS[to];
  const lift = Math.hypot(endX - startX, endY - startY) * LIFT;
  return [(startX + endX) / 2, (startY + endY) / 2 - lift];
}

function lanePath(from: DemoCity, to: DemoCity): string {
  const [startX, startY] = US_CITY_POINTS[from];
  const [endX, endY] = US_CITY_POINTS[to];
  const [controlX, controlY] = laneControl(from, to);
  return `M${startX} ${startY}Q${controlX} ${controlY} ${endX} ${endY}`;
}

function lanePeak(from: DemoCity, to: DemoCity): [number, number] {
  const [startX, startY] = US_CITY_POINTS[from];
  const [endX, endY] = US_CITY_POINTS[to];
  const [controlX, controlY] = laneControl(from, to);
  return [0.25 * startX + 0.5 * controlX + 0.25 * endX, 0.25 * startY + 0.5 * controlY + 0.25 * endY];
}

function mapPosition(point: readonly [number, number]): { left: string; top: string } {
  return { left: `${(point[0] / US_MAP_WIDTH) * 100}%`, top: `${(point[1] / US_MAP_HEIGHT) * 100}%` };
}

function AmbientLanes() {
  return (
    <g fill="none" strokeLinecap="round">
      {AMBIENT_LANES.map((lane, index) => {
        const path = lanePath(lane.from, lane.to);
        return (
          <g key={`${lane.from}-${lane.to}`}>
            <path d={path} className="stroke-brand/30" strokeWidth={1.5} />
            <path d={path} pathLength={1} className="stroke-brand/60 motion-reduce:hidden" strokeWidth={2.5} strokeDasharray="0.18 1">
              <animate attributeName="stroke-dashoffset" from="1.18" to="0" dur={`${lane.seconds}s`} begin={`${(index * 0.7) % 3}s`} repeatCount="indefinite" />
            </path>
          </g>
        );
      })}
    </g>
  );
}

function ActiveLane({ from, to, animated }: { from: DemoCity; to: DemoCity; animated: boolean }) {
  const path = lanePath(from, to);
  const [startX, startY] = US_CITY_POINTS[from];
  const [endX, endY] = US_CITY_POINTS[to];
  return (
    <g fill="none" strokeLinecap="round">
      <circle cx={startX} cy={startY} r={7} className="fill-brand/30 motion-reduce:hidden">
        <animate attributeName="r" values="7;18;7" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.9;0;0.9" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx={startX} cy={startY} r={5} className="fill-brand stroke-surface" strokeWidth={2.5} />
      <path d={path} pathLength={1} className="stroke-brand" strokeWidth={4} strokeDasharray="1 1" strokeDashoffset={0}>
        {animated ? <animate attributeName="stroke-dashoffset" from="1" to="0" dur="1.1s" fill="freeze" /> : null}
      </path>
      <g transform={animated ? undefined : `translate(${endX} ${endY})`}>
        {animated ? <animateMotion path={path} dur={`${TRUCK_SECONDS}s`} fill="freeze" /> : null}
        <g transform={endX < startX ? "scale(-1 1)" : undefined}>
          <circle r={22} className="fill-surface stroke-brand" strokeWidth={3} />
          <Truck x={-14} y={-14} width={28} height={28} className="text-foreground" strokeWidth={2.25} />
        </g>
      </g>
      <circle cx={endX} cy={endY} r={5} className="fill-brand stroke-surface" strokeWidth={2.5} />
    </g>
  );
}

function Nodes() {
  return (
    <g>
      {Object.entries(US_CITY_POINTS).map(([city, [pointX, pointY]]) => (
        <circle key={city} cx={pointX} cy={pointY} r={3.5} className="fill-brand/70 stroke-surface" strokeWidth={1.5} />
      ))}
    </g>
  );
}

type Side = "left" | "right";

function clampSide(side: Side, pointX: number): Side {
  if (pointX < WEST_EDGE) return "right";
  if (pointX > EAST_EDGE) return "left";
  return side;
}

function beside(side: Side, extra: string): string {
  return cn("absolute w-max", side === "left" ? "-translate-x-full pr-4" : "pl-4", extra);
}

function invoicePlacement(scenario: DemoScenario): { className: string; style: CSSProperties } {
  const from = US_CITY_POINTS[scenario.from];
  const to = US_CITY_POINTS[scenario.to];
  const side = clampSide(to[0] >= from[0] ? "right" : "left", to[0]);
  return { className: beside(side, "-translate-y-1/2"), style: mapPosition(to) };
}

interface TicketProps {
  scenario: DemoScenario;
  previous: DemoScenario;
  step: number;
}

function MapLabels({ scenario, previous, step }: TicketProps) {
  const invoice = invoicePlacement(scenario);
  const pillSide: Side = invoice.className.includes("-translate-x-full") ? "right" : "left";
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 max-lg:hidden">
      <FreightPill
        scenario={scenario}
        shown={step >= 1}
        className={beside(pillSide, "-translate-y-1/2")}
        style={mapPosition(lanePeak(scenario.from, scenario.to))}
      />
      <LoadInvoice scenario={previous} shown={step < 3} stamped {...invoicePlacement(previous)} />
      <LoadInvoice scenario={scenario} shown={step >= 3} stamped={step >= 4} {...invoice} />
    </div>
  );
}

function PhoneTicket({ scenario, previous, step }: TicketProps) {
  return (
    <div aria-hidden className="flex flex-col items-start gap-3 lg:hidden">
      <FreightPill scenario={scenario} shown={step >= 1} />
      <div className="grid *:col-start-1 *:row-start-1">
        <LoadInvoice scenario={previous} shown={step < 3} stamped className="mt-2" />
        <LoadInvoice scenario={scenario} shown={step >= 3} stamped={step >= 4} className="mt-2" />
      </div>
    </div>
  );
}

export function LandingHeroScene() {
  const { scenario, typed, sceneIndex, scene, previousScene, sceneStep, animated } = useQuoteDemo();
  return (
    <div className="flex flex-col gap-5">
      <RequestBubble request={scenario.request} typed={typed} />
      <div className="relative">
        <svg viewBox={`0 0 ${US_MAP_WIDTH} ${US_MAP_HEIGHT}`} aria-hidden className="block h-auto w-full">
          <path d={US_DOT_PATH} className="stroke-foreground/40" strokeWidth={4} strokeLinecap="round" />
          <AmbientLanes />
          <Nodes />
          <ActiveLane key={sceneIndex} from={scene.from} to={scene.to} animated={animated} />
        </svg>
        <MapLabels key={sceneIndex} scenario={scene} previous={previousScene} step={sceneStep} />
      </div>
      <PhoneTicket key={sceneIndex} scenario={scene} previous={previousScene} step={sceneStep} />
    </div>
  );
}
